import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getPrinterDetails, updatePrinter } from "../services/printerservice";
import { getClientes } from "../services/clientesservice"; 
import api from "../api/api"; 
import { QRCodeSVG } from "qrcode.react";

export default function ProntuarioUniversalAtivo() { 
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]); 
  
  // Controle de Abas Dinâmicas
  const [activeTab, setActiveTab] = useState('intervencoes');
  const [activeStatusTab, setActiveStatusTab] = useState('pendente');
  const [readingHistory, setReadingHistory] = useState([]);
  
  // Controle de Modais Nativos e Lightbox
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false); 
  const [viewingPhoto, setViewingPhoto] = useState(null); 

  const [editForm, setEditForm] = useState({ 
    model: "", serial_number: "", counter: 0, counter_color: 0, notes: "", client_id: "",
    cylinder_status: "", fuser_status: "", belt_status: "", 
    developer_status: "", rollers_status: "", duplex_status: "", brand: ""
  });
  
  const [notes, setNotes] = useState("");

  useEffect(() => { loadData(); }, [id]);

  async function loadData() {
    try {
      const res = await getPrinterDetails(id);
      const listaClientes = await getClientes(); 
      const resHistory = await api.get(`/printers/${id}/history`);
      
      setData(res);
      setClients(listaClientes);
      setReadingHistory(resHistory.data || []);
      setNotes(res.printer.notes || "");
      
      setEditForm({
        model: res.printer.model || "",
        serial_number: res.printer.serial || "", 
        counter: res.printer.counter || 0,
        counter_color: res.printer.counter_color || 0,
        notes: res.printer.notes || "",
        client_id: res.printer.client_id || "null",
        brand: res.printer.brand || "",
        cylinder_status: res.printer.cylinder_status || "",
        fuser_status: res.printer.fuser_status || "",
        belt_status: res.printer.belt_status || "",
        developer_status: res.printer.developer_status || "",
        rollers_status: res.printer.rollers_status || "",
        duplex_status: res.printer.duplex_status || "",
        diagnostic: res.printer.diagnostic || ""
      });
    } catch (err) {
      console.error("Erro Crítico ao carregar Dossiê do Ativo:", err);
    }
  }

  // 🚀 DRE DO ATIVO: Motor Financeiro de Lucratividade
  const financialData = useMemo(() => {
    if (!data || !readingHistory) return null;

    let totalRevenue = 0;
    let totalCost = 0;
    const monthly = {}; 

    readingHistory.forEach(r => {
      totalRevenue += r.total_value;
      if (!monthly[r.month]) monthly[r.month] = { rev: 0, cost: 0 };
      monthly[r.month].rev += r.total_value;
    });

    data.toners?.forEach(t => {
      const cost = t.cost || 0;
      totalCost += cost;
      
      const d = new Date(t.date);
      const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthly[monthStr]) monthly[monthStr] = { rev: 0, cost: 0 };
      monthly[monthStr].cost += cost;
    });

    const profit = totalRevenue - totalCost;
    const margin = totalRevenue > 0 ? ((profit / totalRevenue) * 100).toFixed(1) : 0;

    const sortedMonths = Object.keys(monthly).sort().slice(-6);
    const chartData = sortedMonths.map(m => ({
      month: m,
      rev: monthly[m].rev,
      cost: monthly[m].cost,
      profit: monthly[m].rev - monthly[m].cost
    }));

    const maxVal = Math.max(...chartData.map(d => Math.max(d.rev, d.cost)), 1);

    return { totalRevenue, totalCost, profit, margin, chartData, maxVal };
  }, [data, readingHistory]);

  const saveInlineChange = async (updatedFields) => {
    try {
      const payload = { ...editForm, ...updatedFields, counter: parseInt(editForm.counter) || 0, counter_color: parseInt(editForm.counter_color) || 0, client_id: editForm.client_id === "null" ? null : parseInt(editForm.client_id) };
      await updatePrinter(id, payload);
      setData(prev => ({ ...prev, printer: { ...prev.printer, ...updatedFields } }));
      setEditForm(prev => ({ ...prev, ...updatedFields }));
    } catch (err) { console.error("Erro ao sincronizar alteração inline:", err); }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(`/printers/${id}/photo`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      loadData();
    } catch (err) { alert("Falha no upload da evidência."); }
  };

  const handlePhotoDelete = async (photoUrl) => {
    if (window.confirm("Deseja apagar definitivamente esta foto do prontuário?")) {
      try { await api.delete(`/printers/${id}/photo`, { data: { photo_path: photoUrl } }); loadData(); } 
      catch (err) { alert("Erro ao apagar arquivo."); }
    }
  };

  async function handleDeleteReading(readingId) {
    if (window.confirm("Atenção: Excluir este faturamento irá impactar os relatórios do mês. Confirmar exclusão?")) {
      try { await api.delete(`/readings/${readingId}`); setReadingHistory(prev => prev.filter(r => r.id !== readingId)); } 
      catch (err) { alert("Falha na exclusão do histórico."); }
    }
  }

  async function handleFullUpdate() {
    try {
      const payload = { ...editForm, notes: notes, counter: parseInt(editForm.counter) || 0, counter_color: parseInt(editForm.counter_color) || 0, client_id: editForm.client_id === "null" ? null : parseInt(editForm.client_id) };
      await updatePrinter(id, payload);
      setIsEditModalOpen(false);
      alert("Ativo atualizado/alocado com sucesso! ⚙️");
      navigate(payload.client_id ? "/printers" : "/laboratorio");
    } catch (err) { alert("Erro ao gravar parâmetros técnicos."); }
  }

  async function handleDelete() {
    if (window.confirm("⚠️ ALERTA: Deseja remover este equipamento e APAGAR TODO o seu histórico (inclusive fotos e faturamentos)? Ação irreversível!")) {
      try { await api.delete(`/printers/${id}`); navigate(data.printer.client_id ? "/printers" : "/laboratorio"); } 
      catch (err) { alert("Erro ao remover ativo da base de dados."); }
    }
  }

  if (!data) return <div className="flex h-screen items-center justify-center text-center font-black animate-pulse text-indigo-600 bg-[#f8fafc] uppercase tracking-widest text-xs">Sincronizando Base de Dados...</div>;

  const currentCounterPB = readingHistory.length > 0 ? readingHistory[0].curr_pb : data.printer.counter;
  const currentCounterColor = readingHistory.length > 0 ? readingHistory[0].curr_color : (data.printer.counter_color || 0);
  const qrCodeUrl = `${window.location.origin}/printers/${id}`;
  const isLab = !data.printer.client_id;
  const locationLabel = isLab ? "🏢 Laboratório FKtechInformatica" : `🏢 ${data.printer.client}`;
  const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

  return (
    <div className="p-4 md:p-8 bg-[#f8fafc] min-h-screen text-slate-800 relative overflow-x-hidden print:p-0 print:bg-white print:text-black">
      
      {/* 🔍 ZOOM LIGHTBOX EM CAMPO */}
      {viewingPhoto && (
        <div className="fixed inset-0 z-[9999] bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4 cursor-zoom-out animate-in fade-in duration-350" onClick={() => setViewingPhoto(null)}>
          <img src={`http://localhost:8000${viewingPhoto}`} alt="Evidência Ampliada" className="max-w-[95vw] max-h-[90vh] object-contain rounded-2xl shadow-2xl animate-in zoom-in-95" />
        </div>
      )}

      {/* Background Orbs */}
      <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] bg-indigo-500/5 blur-[120px] rounded-full pointer-events-none print:hidden"></div>

      <div className="print:hidden max-w-[1600px] mx-auto space-y-8">
        
        {/* HEADER CLARO PREMIUM */}
        <div className="bg-white/80 backdrop-blur-2xl p-6 md:p-8 rounded-[35px] md:rounded-[40px] border border-slate-200/60 shadow-[0_10px_40px_rgba(0,0,0,0.02)] flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6 relative">
          <div className="absolute top-0 left-12 w-24 h-[3px] bg-gradient-to-r from-transparent via-indigo-500 to-transparent"></div>
          
          <div className="flex gap-6 items-center w-full xl:w-auto">
            <div className="w-20 h-20 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl flex items-center justify-center text-white shadow-md text-3xl shrink-0">
              {isLab ? '🛠️' : '🖨️'}
            </div>
            <div>
              <span className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.3em] block mb-1">{data.printer.brand || "KONICA MINOLTA"}</span>
              <h1 className="text-3xl lg:text-4xl font-black text-slate-900 tracking-tighter uppercase leading-none">
                {data.printer.model}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className={`text-[10px] font-black border px-3 py-1 rounded-lg uppercase shadow-sm flex items-center gap-1.5 ${isLab ? 'bg-slate-800 text-white border-slate-700' : 'bg-indigo-50 text-indigo-700 border-indigo-100'}`}>
                  {isLab && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>}
                  {locationLabel}
                </span>
                <span className="text-[10px] font-mono font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded-md border border-slate-200">S/N: {data.printer.serial || "N/A"}</span>
                <span className="text-[10px] font-black text-slate-400 uppercase">ID: #{data.printer.id}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-6 w-full xl:w-auto">
              <div className="flex gap-4 w-full md:w-auto">
                  <div className="bg-slate-50 border border-slate-200/60 p-4 px-6 rounded-2xl text-center w-full shadow-inner">
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{isLab ? 'Contador PB' : 'Medição PB'}</p>
                      <p className="text-2xl font-black text-slate-900 tracking-tighter">{currentCounterPB}</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-200/60 p-4 px-6 rounded-2xl text-center w-full shadow-inner">
                      <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest mb-1">{isLab ? 'Contador Color' : 'Medição Color'}</p>
                      <p className="text-2xl font-black text-indigo-600 tracking-tighter">{currentCounterColor}</p>
                  </div>
              </div>
              
              <div className="flex gap-2 w-full md:w-auto">
                <button onClick={() => setIsQRModalOpen(true)} className="flex-1 md:flex-none bg-slate-100 text-slate-700 border border-slate-200 px-5 py-4 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-white hover:border-slate-300 shadow-sm transition-all active:scale-95">🔳 QR Code</button>
                <button onClick={() => setIsEditModalOpen(true)} className="flex-1 md:flex-none bg-slate-900 text-white px-5 py-4 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-600 shadow-md transition-all active:scale-95">⚙️ {isLab ? 'Identidade' : 'Parâmetros'}</button>
                <button onClick={handleDelete} className="bg-white border border-slate-200 text-rose-500 w-14 flex justify-center items-center rounded-xl font-black text-lg hover:bg-rose-500 hover:text-white transition-all shadow-sm active:scale-95">🗑️</button>
                <Link to={isLab ? "/laboratorio" : "/printers"} className="bg-white border border-slate-200 text-slate-500 w-14 flex justify-center items-center rounded-xl hover:bg-slate-100 transition-all shadow-sm">
                   <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                </Link>
              </div>
          </div>
        </div>

        {/* MAIN CONTAINER GRID */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 items-start">
          
          <div className="space-y-8">
            {/* INLINE HEALTH BOARD */}
            <div className="bg-white border border-slate-200/80 rounded-[35px] md:rounded-[40px] p-6 md:p-8 shadow-sm">
              <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-[0.2em] flex items-center gap-2">
                      <span className="text-lg">🩺</span> Raio-X de Insumos
                  </h3>
                  <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50 px-2 py-0.5 rounded border border-slate-200">Auto-Save Ativo</span>
              </div>
              <div className="space-y-4">
                  {[
                    { id: 'cylinder_status', label: 'Cilindro (Drum)' },
                    { id: 'fuser_status', label: 'Unidade de Fusão' },
                    { id: 'belt_status', label: 'Esteira (Belt)' },
                    { id: 'developer_status', label: 'Unidade Reveladora' },
                    { id: 'rollers_status', label: 'Roletes de Alimentação' },
                    { id: 'duplex_status', label: 'Unidade Duplex' }
                  ].map((part) => {
                    return (
                      <div key={part.id} className="p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:border-indigo-200 flex flex-col md:flex-row md:items-center gap-3 transition-all group shadow-sm">
                        <span className="text-[11px] font-black uppercase text-slate-600 group-hover:text-indigo-700 transition-colors md:w-1/3 shrink-0">
                            {part.label}
                        </span>
                        <input 
                            type="text"
                            placeholder="Ex: OK, Trocado em 12/05, Película gasta..."
                            value={editForm[part.id] || ''} 
                            onChange={(e) => setEditForm({ ...editForm, [part.id]: e.target.value })}
                            onBlur={(e) => saveInlineChange({ [part.id]: e.target.value })}
                            className="flex-1 px-4 py-2.5 rounded-xl font-bold text-xs text-slate-700 border border-slate-200 outline-none focus:ring-2 ring-indigo-500/20 focus:border-indigo-500 bg-white transition-all w-full placeholder:font-normal placeholder:text-slate-400"
                        />
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* 💰 DRE DO ATIVO - ANÁLISE DE LUCRATIVIDADE */}
            {financialData && (
              <div className="bg-white border border-slate-200/80 rounded-[35px] md:rounded-[40px] p-6 md:p-8 shadow-sm">
                <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
                    <h3 className="text-sm font-black text-slate-800 uppercase tracking-[0.2em] flex items-center gap-2">
                        <span className="text-lg">📈</span> DRE do Ativo (Lucratividade)
                    </h3>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-slate-50 border border-slate-100 p-4 rounded-2xl">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Receita Gerada</p>
                    <p className="text-lg font-black text-indigo-600 truncate">{fmt(financialData.totalRevenue)}</p>
                  </div>
                  <div className="bg-rose-50/50 border border-rose-100 p-4 rounded-2xl">
                    <p className="text-[9px] font-black text-rose-400 uppercase tracking-widest mb-1">Custo Insumos</p>
                    <p className="text-lg font-black text-rose-600 truncate">{fmt(financialData.totalCost)}</p>
                  </div>
                  <div className={`col-span-2 md:col-span-1 p-4 rounded-2xl border shadow-inner ${financialData.profit >= 0 ? 'bg-emerald-50/50 border-emerald-100' : 'bg-rose-50/50 border-rose-100'}`}>
                    <p className={`text-[9px] font-black uppercase tracking-widest mb-1 ${financialData.profit >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>Lucro Líquido</p>
                    <p className={`text-lg font-black truncate ${financialData.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {fmt(financialData.profit)} <span className="text-[10px] ml-1 opacity-70">({financialData.margin}%)</span>
                    </p>
                  </div>
                </div>

                {/* GRÁFICO DE BARRAS NATIVO (CSS) */}
                <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 h-[220px] flex items-end justify-between gap-2 md:gap-6">
                  {financialData.chartData.length === 0 ? (
                    <div className="w-full h-full flex items-center justify-center text-[10px] font-black uppercase text-slate-400 tracking-widest">Sem dados financeiros recentes</div>
                  ) : (
                    financialData.chartData.map((d, i) => {
                      const revHeight = Math.max((d.rev / financialData.maxVal) * 100, 2);
                      const costHeight = Math.max((d.cost / financialData.maxVal) * 100, 2);
                      
                      return (
                        <div key={i} className="flex flex-col items-center flex-1 group">
                          <div className="w-full flex justify-center items-end gap-1 h-[140px] relative">
                            {/* Bar Receita */}
                            <div className="w-full max-w-[20px] bg-indigo-400 rounded-t-md transition-all duration-700 ease-out group-hover:bg-indigo-500" style={{ height: `${revHeight}%` }} title={`Receita: ${fmt(d.rev)}`}></div>
                            {/* Bar Custo */}
                            <div className="w-full max-w-[20px] bg-rose-400 rounded-t-md transition-all duration-700 ease-out group-hover:bg-rose-500" style={{ height: `${costHeight}%` }} title={`Custo: ${fmt(d.cost)}`}></div>
                          </div>
                          <span className="text-[8px] font-black text-slate-400 uppercase mt-3 tracking-wider">{d.month.split('-').reverse().join('/')}</span>
                        </div>
                      );
                    })
                  )}
                </div>

              </div>
            )}
          </div>

          {/* RIGHT: LIVE TRIAGEM LAUDO, PHOTOS & TABS */}
          <div className="space-y-6">
              
              {/* LAUDO DE TRIAGEM ESCURO */}
              <div className="bg-[#0f172a] border border-slate-800 rounded-[35px] md:rounded-[40px] p-6 md:p-8 shadow-xl relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-[40px] pointer-events-none"></div>
                  <div className="flex justify-between items-center mb-4">
                      <h3 className="text-[10px] font-black text-amber-500 uppercase tracking-[0.3em] flex items-center gap-2">
                        📋 Laudo Técnico {isLab ? 'de Triagem' : 'de Campo'}
                      </h3>
                  </div>
                  <textarea 
                    className="w-full bg-slate-950/40 border border-slate-800 hover:border-amber-500/30 focus:border-amber-500/50 rounded-2xl p-5 text-sm font-medium text-slate-200 outline-none transition-all h-44 resize-none placeholder:text-slate-700 custom-scrollbar font-sans shadow-inner leading-relaxed"
                    placeholder="Descreva falhas, IPs de rede, senhas de acesso, ou testes executados na bancada..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    onBlur={(e) => saveInlineChange({ notes: e.target.value })}
                  />
              </div>

              {/* GALERIA DE EVIDÊNCIAS */}
              <div className="bg-white border border-slate-200 rounded-[35px] md:rounded-[40px] p-6 md:p-8 shadow-sm">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 border-b border-slate-100 pb-4">
                    <div>
                        <h3 className="text-[11px] font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">📷 Evidências do Ativo</h3>
                        <p className="text-[9px] text-slate-400 font-bold uppercase mt-1">Fotos vinculadas à máquina (Bancada + Campo)</p>
                    </div>
                    <label className="w-full sm:w-auto bg-indigo-600 text-white px-5 py-3 rounded-xl text-[9px] font-black uppercase tracking-widest cursor-pointer hover:bg-slate-900 transition-all shadow-md active:scale-95 text-center flex justify-center items-center gap-2">
                      <span>📸</span> Anexar Foto
                      <input type="file" className="hidden" onChange={(e) => handlePhotoUpload(e.target.files[0])} accept="image/*" />
                    </label>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-h-[260px] overflow-y-auto custom-scrollbar pr-2">
                      {data.printer.photos?.length > 0 ? data.printer.photos.map((url, idx) => (
                          <div key={idx} className="relative group aspect-square rounded-2xl overflow-hidden border border-slate-200 bg-slate-50">
                              <img src={`http://localhost:8000${url}`} alt="Evidência" className="object-cover w-full h-full cursor-zoom-in transition-transform duration-300 group-hover:scale-110" onClick={() => setViewingPhoto(url)} />
                              <button onClick={() => handlePhotoDelete(url)} className="absolute bottom-2 right-2 bg-rose-500 text-white w-8 h-8 flex items-center justify-center rounded-lg opacity-0 group-hover:opacity-100 shadow-md text-xs transition-opacity">🗑️</button>
                          </div>
                      )) : (
                          <div className="col-span-full flex flex-col items-center justify-center py-8 bg-slate-50 border border-dashed border-slate-200 rounded-2xl">
                             <p className="text-slate-400 font-black uppercase tracking-widest text-[9px]">Nenhuma foto anexada a este prontuário.</p>
                          </div>
                      )}
                  </div>
              </div>

              {/* TABS DE HISTÓRICOS (MANUTENÇÃO, FATURAMENTO, SUPRIMENTOS) */}
              <div className="bg-white border border-slate-200 rounded-[35px] md:rounded-[40px] p-6 flex flex-col shadow-sm">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 border-b border-slate-100 pb-4 gap-4 md:gap-0">
                  <div className="flex gap-4 md:gap-6 relative overflow-x-auto w-full custom-scrollbar pb-1">
                     <button onClick={() => setActiveTab('intervencoes')} className={`text-xs font-black tracking-widest transition-all uppercase pb-2 relative whitespace-nowrap ${activeTab === 'intervencoes' ? 'text-slate-900 border-b-2 border-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}>Manutenções</button>
                     <button onClick={() => setActiveTab('historico')} className={`text-xs font-black tracking-widest transition-all uppercase pb-2 relative whitespace-nowrap ${activeTab === 'historico' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}>Faturamentos ({readingHistory.length})</button>
                     <button onClick={() => setActiveTab('suprimentos')} className={`text-xs font-black tracking-widest transition-all uppercase pb-2 relative whitespace-nowrap ${activeTab === 'suprimentos' ? 'text-emerald-600 border-b-2 border-emerald-600' : 'text-slate-400 hover:text-slate-600'}`}>Suprimentos ({data.toners?.length || 0})</button>
                  </div>
                  {activeTab === 'intervencoes' && (
                     <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200/40 shrink-0">
                         <button onClick={() => setActiveStatusTab('pendente')} className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all ${activeStatusTab === 'pendente' ? 'bg-white shadow-sm text-rose-600' : 'text-slate-500'}`}>Abertas</button>
                         <button onClick={() => setActiveStatusTab('resolvido')} className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all ${activeStatusTab === 'resolvido' ? 'bg-white shadow-sm text-emerald-600' : 'text-slate-500'}`}>Prontas</button>
                     </div>
                  )}
                </div>

                <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                  
                  {/* ABA: MANUTENÇÕES */}
                  {activeTab === 'intervencoes' && (
                    <div className="space-y-3">
                      {(activeStatusTab === 'pendente' ? data.open_maint : data.resolved_maint).map((m, i) => (
                        <div key={i} className={`p-4 rounded-xl border ${activeStatusTab === 'pendente' ? 'bg-rose-50/60 border-rose-100 text-rose-900' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-black text-xs uppercase">{m.issue}</span>
                            <span className="text-[9px] font-mono bg-white px-2 py-1 rounded border border-slate-200">{new Date(m.date).toLocaleDateString()}</span>
                          </div>
                          {m.solution && (
                            <div className="mt-2 pt-2 border-t border-slate-200 text-slate-500 text-xs italic">
                              "{m.solution}"
                            </div>
                          )}
                        </div>
                      ))}
                      {(activeStatusTab === 'pendente' ? data.open_maint : data.resolved_maint).length === 0 && (
                         <p className="text-center py-8 text-slate-400 font-bold uppercase text-[10px] tracking-wider">Nenhum registro encontrado.</p>
                      )}
                    </div>
                  )}

                  {/* ABA: FATURAMENTOS */}
                  {activeTab === 'historico' && (
                    <div className="space-y-3">
                      {readingHistory.length === 0 ? (
                         <p className="text-center py-8 text-slate-400 font-bold uppercase text-[10px] tracking-wider">Aguardando fechamento de leituras.</p>
                      ) : (
                        <table className="w-full text-left">
                          <thead className="text-[8px] font-black uppercase text-slate-400 tracking-widest bg-slate-50">
                            <tr>
                              <th className="p-3 rounded-l-lg">Mês</th>
                              <th className="p-3 text-center">P&B</th>
                              <th className="p-3 text-center">Color</th>
                              <th className="p-3 text-right">Faturado</th>
                              <th className="p-3 text-center w-10 rounded-r-lg">Ação</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {readingHistory.map(hist => (
                                <tr key={hist.id} className="hover:bg-slate-50">
                                  <td className="p-3 text-xs font-bold text-indigo-600">{hist.month}</td>
                                  <td className="p-3 text-center text-xs font-bold text-slate-800">{hist.curr_pb}</td>
                                  <td className="p-3 text-center text-xs font-bold text-indigo-500">{hist.curr_color}</td>
                                  <td className="p-3 text-right text-xs font-black text-emerald-600">R$ {Number(hist.total_value || 0).toFixed(2)}</td>
                                  <td className="p-3 text-center"><button onClick={() => handleDeleteReading(hist.id)} className="text-slate-400 hover:text-rose-500 transition-colors">🗑️</button></td>
                                </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                  {/* ABA: SUPRIMENTOS (TONERS ENTREGUES) */}
                  {activeTab === 'suprimentos' && (
                    <div className="space-y-3">
                      {data.toners?.length === 0 ? (
                         <p className="text-center py-8 text-slate-400 font-bold uppercase text-[10px] tracking-wider">Nenhum envio de insumo registrado.</p>
                      ) : (
                        <table className="w-full text-left">
                          <thead className="text-[8px] font-black uppercase text-slate-400 tracking-widest bg-slate-50">
                            <tr>
                              <th className="p-3 rounded-l-lg">Data</th>
                              <th className="p-3">Modelo / Toner</th>
                              <th className="p-3 text-center">Qtd</th>
                              <th className="p-3 rounded-r-lg">Observação</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {data.toners.map((t, idx) => (
                              <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                <td className="p-3 text-[10px] font-mono font-bold text-slate-500">{new Date(t.date).toLocaleDateString()}</td>
                                <td className="p-3 text-xs font-black text-indigo-600">{t.model}</td>
                                <td className="p-3 text-center text-xs font-bold text-slate-800">
                                  <span className="bg-slate-200/60 px-2 py-0.5 rounded-md">{t.qty}</span>
                                </td>
                                <td className="p-3 text-[10px] font-bold text-slate-500 italic max-w-[150px] truncate" title={t.obs}>{t.obs || "---"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                </div>
              </div>

          </div>
        </div>
      </div>

      {/* 🚀 OVERLAYS NATIVOS: SUBSTITUEM O MODAL GENÉRICO E EVITAM O "DUPLO X" */}
      
      {/* OVERLAY: ALOCAÇÃO / PARÂMETROS */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/40 backdrop-blur-sm flex justify-center items-center p-4 animate-in fade-in duration-300 print:hidden">
          <div className="bg-white rounded-[35px] w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-6 md:p-8 max-h-[85vh] overflow-y-auto custom-scrollbar text-slate-800">
              <div className="flex justify-between items-center mb-6 border-b border-slate-200 pb-4">
                <div>
                  <h2 className="text-2xl font-black uppercase tracking-tighter text-slate-900">Alocação & Parâmetros</h2>
                  <p className="text-[10px] text-indigo-600 font-black uppercase tracking-widest mt-0.5">Movimentação do Ativo e Identidade Base</p>
                </div>
                <button onClick={() => setIsEditModalOpen(false)} className="text-slate-400 hover:text-rose-500 text-2xl font-black transition-colors px-2">✕</button>
              </div>
              
              <div className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] font-black uppercase text-indigo-600 mb-1 block ml-1">Destino do Equipamento</label>
                    <select className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl font-black text-sm text-slate-800 outline-none focus:ring-2 ring-indigo-500/20 cursor-pointer uppercase shadow-inner" value={editForm.client_id} onChange={e => setEditForm({...editForm, client_id: e.target.value})}>
                      <option value="null">🏢 ESTOQUE CENTRAL / LABORATÓRIO FKTECHINFORMATICA</option>
                      {clients.map(c => <option key={c.id} value={c.id}>👤 {c.name.toUpperCase()}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-black uppercase text-slate-500 mb-1 block ml-1">Marca</label>
                      <input className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl font-black text-slate-800 uppercase outline-none focus:ring-2 ring-indigo-500/20 focus:border-indigo-500" value={editForm.brand} onChange={e => setEditForm({...editForm, brand: e.target.value})} placeholder="Ex: SAMSUNG" />
                    </div>
                    <div>
                      <label className="text-[10px] font-black uppercase text-slate-500 mb-1 block ml-1">Modelo Comercial</label>
                      <input className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl font-black text-slate-800 uppercase outline-none focus:ring-2 ring-indigo-500/20 focus:border-indigo-500" value={editForm.model} onChange={e => setEditForm({...editForm, model: e.target.value})} />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase text-slate-500 mb-1 block ml-1">Número de Série</label>
                    <input className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold text-slate-600 uppercase outline-none focus:ring-2 ring-indigo-500/20 focus:border-indigo-500" value={editForm.serial_number} onChange={e => setEditForm({...editForm, serial_number: e.target.value})} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <div>
                      <label className="text-[10px] font-black uppercase text-slate-600 mb-1 block text-center">Contador Base P&B</label>
                      <input type="number" className="w-full p-3 bg-white border border-slate-200 rounded-lg font-black text-slate-900 text-center outline-none focus:ring-2 ring-indigo-500/20 focus:border-indigo-500" value={editForm.counter} onChange={e => setEditForm({...editForm, counter: e.target.value})} />
                    </div>
                    <div>
                      <label className="text-[10px] font-black uppercase text-indigo-600 mb-1 block text-center">Contador Base Color</label>
                      <input type="number" className="w-full p-3 bg-white border border-slate-200 rounded-lg font-black text-indigo-600 text-center outline-none focus:ring-2 ring-indigo-500/20 focus:border-indigo-500" value={editForm.counter_color} onChange={e => setEditForm({...editForm, counter_color: e.target.value})} />
                    </div>
                </div>

                <button onClick={handleFullUpdate} className="w-full py-5 bg-slate-900 text-white font-black rounded-xl shadow-lg hover:bg-indigo-600 uppercase text-[11px] tracking-widest transition-all active:scale-95">
                  Confirmar Parâmetros e Alocação 💾
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OVERLAY: QR CODE FOCADO PARA ETIQUETA TÉRMICA */}
      {isQRModalOpen && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/40 backdrop-blur-sm flex justify-center items-center p-4 animate-in fade-in duration-300 print:bg-white print:backdrop-blur-none print:p-0">
          
          {/* CSS para limpar margens do navegador na hora de imprimir a etiqueta térmica */}
          <style>
            {`
              @media print {
                @page { margin: 0mm; size: auto; }
                body { background: white; }
              }
            `}
          </style>

          <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl relative text-slate-900 animate-in zoom-in-95 duration-300 print:shadow-none print:w-full print:max-w-none print:p-4 print:flex print:flex-col print:items-center print:justify-center print:h-screen print:rounded-none">
            
            {/* OCULTA NA IMPRESSÃO */}
            <button onClick={() => setIsQRModalOpen(false)} className="absolute top-4 right-4 text-slate-400 hover:text-rose-500 text-2xl font-black px-2 print:hidden">✕</button>
            <div className="flex flex-col items-center mb-6 print:hidden">
              <div className="text-3xl font-black text-slate-900 flex items-center gap-2"><span className="bg-indigo-600 text-white w-8 h-8 rounded-lg flex items-center justify-center text-xl">I</span>INSYS</div>
            </div>

            {/* VISÍVEL NA IMPRESSÃO (MODELO E SERIAL MAIORES) */}
            <div className="text-center mb-4 border-y border-dashed border-slate-200 py-4 print:border-none print:py-0 print:mb-6">
              <h2 className="text-2xl font-black uppercase text-slate-800 leading-tight print:text-4xl">{data.printer.model}</h2>
              <p className="text-sm font-mono font-black text-slate-900 mt-1 print:text-2xl">S/N: {data.printer.serial}</p>
            </div>

            {/* QR CODE SEM BORDA/PADDING EXCESSIVO */}
            <div className="flex justify-center mb-4 print:mb-0 print:scale-125">
               <div className="p-0">
                 <QRCodeSVG value={qrCodeUrl} size={180} level="H" />
               </div>
            </div>

            {/* OCULTA NA IMPRESSÃO */}
            <button onClick={() => window.print()} className="w-full mt-8 py-4 bg-indigo-600 text-white font-black rounded-xl uppercase text-[11px] tracking-widest print:hidden active:scale-95">🖨️ Imprimir Etiqueta</button>
          </div>
        </div>
      )}

    </div>
  );
}